import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import {
  getDisplayName,
  getInitials,
  useRoster,
  type HealthStatus,
  type RosterFilters,
  type RosterMember,
} from '@/hooks/useRoster';
import type { RosterSidebarGroup } from './sidebar/RosterGroupListItem';

const HEALTH_LABELS: Record<HealthStatus, { label: string; className: string }> = {
  healthy: { label: 'Healthy', className: 'bg-green-100 text-green-700' },
  needs_attention: { label: 'Needs Attention', className: 'bg-amber-100 text-amber-700' },
  at_risk: { label: 'At Risk', className: 'bg-red-100 text-red-700' },
};

function MemberRow({
  member,
  isLeader,
  onClick,
}: {
  member: RosterMember;
  isLeader: boolean;
  onClick: () => void;
}) {
  const health = member.healthStatus ? HEALTH_LABELS[member.healthStatus] : null;
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-xl border border-flock-gray-200 bg-white p-3 text-left shadow-sm transition hover:border-flock-navy-300"
    >
      {member.profileImageUrl ? (
        <img src={member.profileImageUrl} alt="" className="h-10 w-10 rounded-full object-cover" />
      ) : (
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-flock-navy-400 text-sm font-semibold text-white">
          {getInitials(member)}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-flock-navy-900">
          {getDisplayName(member)}
        </div>
        <div className="truncate text-xs text-flock-navy-400">{member.email}</div>
      </div>
      {isLeader && (
        <div className="flex shrink-0 items-center gap-3">
          {member.attendanceRate != null && (
            <span className="text-xs text-flock-navy-500" title="Attendance rate">
              {Math.round(member.attendanceRate)}%
            </span>
          )}
          {health && (
            <span
              className={clsx(
                'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                health.className
              )}
            >
              {health.label}
            </span>
          )}
        </div>
      )}
    </button>
  );
}

/** Group roster pane — group comes from the roster rail (route-driven). */
export default function RosterPage({ group }: { group: RosterSidebarGroup }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [healthFilter, setHealthFilter] = useState<HealthStatus | ''>('');
  const [sort, setSort] = useState<RosterFilters['sort']>('name_asc');

  const filters: RosterFilters = {
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(healthFilter ? { healthStatus: healthFilter } : {}),
    sort,
  };
  const rosterQuery = useRoster(group.id, filters);
  const members = rosterQuery.data?.members ?? [];
  const isLeader = rosterQuery.data?.isLeader ?? false;

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-flock-gray-200 bg-white px-6 py-4">
        <h1 className="text-lg font-semibold text-flock-navy-900">Roster</h1>
        <p className="text-xs text-flock-navy-400">
          {group.name}
          {members.length > 0 ? ` · ${members.length} members` : ''}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-flock-navy-300" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email"
              className="w-64 rounded-lg border border-flock-gray-200 bg-flock-navy-50 py-2 pl-9 pr-3 text-sm outline-none focus:border-flock-navy-400"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as RosterFilters['sort'])}
            className="rounded-lg border border-flock-gray-200 bg-white px-2 py-2 text-sm text-flock-navy-700"
          >
            <option value="name_asc">Name A→Z</option>
            <option value="name_desc">Name Z→A</option>
          </select>
          {isLeader && (
            <select
              value={healthFilter}
              onChange={(e) => setHealthFilter(e.target.value as HealthStatus | '')}
              className="rounded-lg border border-flock-gray-200 bg-white px-2 py-2 text-sm text-flock-navy-700"
            >
              <option value="">All health statuses</option>
              <option value="healthy">Healthy</option>
              <option value="needs_attention">Needs attention</option>
              <option value="at_risk">At risk</option>
            </select>
          )}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="mx-auto max-w-2xl space-y-2">
          {rosterQuery.isLoading && (
            <div className="py-12 text-center text-sm text-flock-navy-400">Loading roster…</div>
          )}
          {rosterQuery.isError && (
            <div className="py-4 text-center text-sm text-red-600">
              Couldn&apos;t load the roster. Try again?
            </div>
          )}
          {!rosterQuery.isLoading && members.length === 0 && (
            <div className="py-12 text-center text-sm text-flock-navy-400">
              {search ? 'No members match your search.' : 'No members yet.'}
            </div>
          )}
          {members.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              isLeader={isLeader}
              onClick={() => navigate(`/roster/group/${group.id}/member/${member.id}`)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
